import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CarCard from "@/components/CarCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, Frown, Car, PlusCircle } from "lucide-react";
import { INDIA_STATES } from "@/data/indiaLocations";

const BRANDS = ["Maruti Suzuki", "Hyundai", "Tata", "Mahindra", "Honda", "Toyota", "Kia", "Volkswagen", "BMW", "Mercedes-Benz"];
const BODY = ["Hatchback", "Sedan", "SUV", "MUV", "Luxury"];
const FUELS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANS = ["Manual", "Automatic"];
const ANY = "any";

export default function Motors() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(ANY);
  const [body, setBody] = useState(ANY);
  const [fuel, setFuel] = useState(ANY);
  const [trans, setTrans] = useState(ANY);
  const [state, setState] = useState(ANY);
  const [maxPrice, setMaxPrice] = useState("");
  const [q, setQ] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (brand !== ANY) params.brand = brand;
    if (body !== ANY) params.body_type = body;
    if (fuel !== ANY) params.fuel_type = fuel;
    if (trans !== ANY) params.transmission = trans;
    if (state !== ANY) params.state = state;
    if (maxPrice) params.max_price = maxPrice;
    if (q) params.q = q;
    const res = await api.get("/vehicles", { params });
    setVehicles(res.data);
    setLoading(false);
  }, [brand, body, fuel, trans, state, maxPrice, q]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-dark flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> VYAPAAR Motors</p>
          <h1 className="mt-1.5 font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Used & new cars from verified sellers</h1>
        </div>
        <Button data-testid="motors-sell-btn" onClick={() => navigate("/motors/post")} className="bg-gold hover:bg-gold-dark text-navy font-semibold">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Sell your car
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-4 text-slate-700">
          <SlidersHorizontal className="h-4 w-4" /><span className="text-sm font-semibold">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger data-testid="car-filter-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value={ANY}>All Brands</SelectItem>{BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={body} onValueChange={setBody}>
            <SelectTrigger data-testid="car-filter-body"><SelectValue placeholder="Body" /></SelectTrigger>
            <SelectContent><SelectItem value={ANY}>All Types</SelectItem>{BODY.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fuel} onValueChange={setFuel}>
            <SelectTrigger data-testid="car-filter-fuel"><SelectValue placeholder="Fuel" /></SelectTrigger>
            <SelectContent><SelectItem value={ANY}>All Fuels</SelectItem>{FUELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={trans} onValueChange={setTrans}>
            <SelectTrigger data-testid="car-filter-trans"><SelectValue placeholder="Gearbox" /></SelectTrigger>
            <SelectContent><SelectItem value={ANY}>Any</SelectItem>{TRANS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="car-filter-state"><SelectValue placeholder="State/UT" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value={ANY}>All States/UTs</SelectItem>{INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input data-testid="car-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchData()} placeholder="Search…" />
            <Button data-testid="car-search-btn" onClick={fetchData} className="bg-navy hover:bg-navy-dark shrink-0"><Search className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <p className="mt-6 mb-4 text-sm text-slate-500" data-testid="car-count">{loading ? "Loading…" : `${vehicles.length} cars found`}</p>

      {!loading && vehicles.length === 0 ? (
        <div className="py-24 text-center"><Frown className="h-12 w-12 text-slate-300 mx-auto" /><p className="mt-4 text-slate-500">No cars match your filters.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {vehicles.map((v, i) => <CarCard key={v.id} vehicle={v} index={i} />)}
        </div>
      )}
    </div>
  );
}
